import * as React from 'react';
import { useState, useEffect, useCallback, Suspense, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { SelectionActionBar } from '@/components/ui/selection-action-bar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  UserIcon,
} from '@heroicons/react/24/solid';
import { LinkIcon } from '@heroicons/react/24/outline';
import { PanelLeft } from 'lucide-react';
import { toast } from 'sonner';
import { peopleService, type Person, type PersonFilters, type FilterOptions, type PeopleRequestError, type ProspeoSuggestionType, type TitleMatchMode } from '@/lib/services/people';
import { PageHeader } from '@/components/ui/page-header';
import { PageScrollArea } from '@/components/ui/page-shell';
import TrialLimitReached from '@/components/TrialLimitReached';
import { AddToCampaignModal, type ProspectForCampaign } from '@/components/campaigns';
import {
  DataTable,
  DataTablePagination,
  useDataTableSelection,
  useDataTableColumns,
  type ColumnDefinition,
  type RowAction
} from '@/components/data-table';

interface AppliedFilter {
  type: string;
  value: string;
  label: string;
  prefix?: string;
}

const STORAGE_KEY = 'findPeople_visibleColumns';

function toTitleCase(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function GovCrownIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 64 60" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="17.6" r="3.7"/><circle cx="10.2" cy="23.5" r="3.7"/><circle cx="3.7" cy="33.2" r="3.7"/><circle cx="31.7" cy="30.6" r="3.7"/><circle cx="43.3" cy="17.6" r="3.7"/><circle cx="53.2" cy="23.5" r="3.7"/><circle cx="59.7" cy="33.2" r="3.7"/>
      <path d="M33.1,9.8c.2-.1.3-.3.5-.5l4.6,2.4v-6.8l-4.6,1.5c-.1-.2-.3-.3-.5-.5l1.9-5.9h-6.7l1.9,5.9c-.2.1-.3.3-.5.5l-4.6-1.5v6.8l4.6-2.4c.1.2.3.3.5.5l-2.6,8c-.9,2.8,1.2,5.7,4.1,5.7h0c3,0,5.1-2.9,4.1-5.7l-2.6-8ZM37,37.9s-3.4,3.8-4.1,6.1c2.2,0,4.2-.5,6.4-2.8l-.7,8.5c-2-2.8-4.4-4.1-5.7-3.8.1,3.1.5,6.7,5.8,7.2,3.7.3,6.7-1.5,7-3.8.4-2.6-2-4.3-3.7-1.6-1.4-4.5,2.4-6.1,4.9-3.2-1.9-4.5-1.8-7.7,2.4-10.9,3,4,2.6,7.3-1.2,11.1,2.4-1.3,6.2,0,4,4.6-1.2-2.8-3.7-2.2-4.2.2-.3,1.7.7,3.7,3,4.2,1.9.3,4.7-.9,7-5.9-1.3,0-2.4.7-3.9,1.7l2.4-8c.6,2.3,1.4,3.7,2.2,4.5.6-1.6.5-2.8,0-5.3l5,1.8c-2.6,3.6-5.2,8.7-7.3,17.5-7.4-1.1-15.7-1.7-24.5-1.7h0c-8.8,0-17.1.6-24.5,1.7-2.1-8.9-4.7-13.9-7.3-17.5l5-1.8c-.5,2.5-.6,3.7,0,5.3.8-.8,1.6-2.3,2.2-4.5l2.4,8c-1.5-1-2.6-1.7-3.9-1.7,2.3,5,5.2,6.2,7,5.9,2.3-.4,3.3-2.4,3-4.2-.5-2.4-3-3.1-4.2-.2-2.2-4.6,1.6-6,4-4.6-3.7-3.7-4.2-7.1-1.2-11.1,4.2,3.2,4.3,6.4,2.4,10.9,2.5-2.8,6.3-1.3,4.9,3.2-1.8-2.7-4.1-1-3.7,1.6.3,2.3,3.3,4.1,7,3.8,5.4-.5,5.7-4.2,5.8-7.2-1.3-.2-3.7,1-5.7,3.8l-.7-8.5c2.2,2.3,4.2,2.7,6.4,2.8-.7-2.3-4.1-6.1-4.1-6.1h10.6,0Z"/>
    </svg>
  );
}

function LinkedInIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
    </svg>
  );
}

const PAGE_SIZE = 25;

/** Prospeo's seniority and headcount vocabularies, offered when searching live. */
const PROSPEO_SENIORITIES = ['Founder/Owner', 'C-Suite', 'Partner', 'Vice President', 'Head', 'Director', 'Manager', 'Senior', 'Entry', 'Intern'];
const PROSPEO_DEPARTMENTS = ['C-Suite', 'Product', 'Engineering & Technical', 'Design', 'Education & Coaching', 'Finance', 'Human Resources', 'Information Technology', 'Legal', 'Marketing', 'Medical & Health', 'Consulting', 'Sales', 'Operations'];
/** Prospeo's own employee brackets — finer than the buckets the saved database uses. */
const PROSPEO_COMPANY_SIZES = ['1-10', '11-20', '21-50', '51-100', '101-200', '201-500', '501-1000', '1001-2000', '2001-5000', '5001-10000', '10000+'].map(v => ({ value: v, label: v }));

const TITLE_MATCH_MODES: { value: TitleMatchMode; label: string; hint: string }[] = [
  { value: 'CONTAINS', label: 'Contains', hint: 'Founder also matches Co-Founder and Founder & CEO.' },
  { value: 'EXACT', label: 'Exact', hint: 'Only the exact title, nothing longer.' },
  { value: 'SIMILAR', label: 'Similar', hint: 'Includes titles that mean roughly the same thing.' },
  { value: 'STRICT', label: 'Strict', hint: 'Tightest matching, fewest results.' },
];

/**
 * Option loaders for the live pickers. Defined at module scope so their
 * identity is stable — Combobox re-runs its lookup whenever the callback
 * changes, and an inline arrow would refetch on every keystroke.
 */
const suggestionLoader = (type: ProspeoSuggestionType) => async (query: string) =>
  (await peopleService.prospeoSuggestions(type, query)).map(s => ({ value: s.value, label: s.label }));
const SUGGEST_LOCATION = suggestionLoader('location');
const SUGGEST_JOB_TITLE = suggestionLoader('job_title');
const SUGGEST_INDUSTRY = suggestionLoader('industry');
const SUGGEST_NAICS = suggestionLoader('naics');
const SUGGEST_SIC = suggestionLoader('sic');

function FilterSectionHeading({ children }: { children: React.ReactNode }) {
  return <p className="text-sm font-semibold pl-4 pt-3 pb-1 not-first:mt-3">{children}</p>;
}

/** Identity of a filter set, ignoring pagination. */
function filterSignature(f: PersonFilters): string {
  const { page: _page, limit: _limit, ...rest } = f;
  return JSON.stringify(Object.entries(rest).sort(([a], [b]) => a.localeCompare(b)));
}

/** Header chip for a single numeric filter. */
function numberChip(type: string, value: number | undefined, prefix: string) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return [];
  return [{ type, value: String(value), label: `${prefix} ${value}`, prefix }];
}

/** Collapsed-panel summary for a min/max pair. */
function rangeChips(min: number | undefined, max: number | undefined, unit: string): { value: string; label: string }[] {
  const hasMin = typeof min === 'number' && Number.isFinite(min);
  const hasMax = typeof max === 'number' && Number.isFinite(max);
  if (!hasMin && !hasMax) return [];
  const suffix = unit ? ` ${unit}` : '';
  const label = hasMin && hasMax ? `${min} to ${max}${suffix}` : hasMin ? `${min}${suffix} or more` : `Up to ${max}${suffix}`;
  return [{ value: 'range', label }];
}

/** A live search needs something to search on; an empty query would just burn a credit. */
const hasProspeoFilters = (f: PersonFilters): boolean =>
  Boolean(
    f.job_titles?.length || f.title || f.seniority?.length || f.department?.length ||
    f.companySize?.length || f.keywords?.length || f.industry?.length ||
    f.location?.length || f.company_location?.length ||
    f.company_names?.length || f.company_domains?.length || f.companyContains ||
    f.naics?.length || f.sic?.length ||
    typeof f.founded_after === 'number' || typeof f.founded_before === 'number' ||
    typeof f.experience_min === 'number' || typeof f.experience_max === 'number',
  );

/** Filters that live in the URL as repeated string params. */
const URL_LIST_PARAMS = [
  'location', 'location_exclude', 'job_titles', 'job_titles_exclude', 'industry', 'industry_exclude',
  'seniority', 'department', 'keywords', 'keywords_exclude', 'companySize', 'company_location',
  'company_names', 'company_domains', 'naics', 'sic', 'enrichmentStatus',
] as const;

/** Filters that live in the URL as single numbers. */
const URL_NUMBER_PARAMS = ['founded_after', 'founded_before', 'experience_min', 'experience_max', 'max_per_company'] as const;

function FindPeoplePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({});
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [addToCampaignOpen, setAddToCampaignOpen] = useState(false);
  const [prospectsForCampaign, setProspectsForCampaign] = useState<ProspectForCampaign[]>([]);
  const [defaultEnrich, setDefaultEnrich] = useState(false);
  // Signature of the filters the current results belong to, so closing the
  // filter modal only re-searches when something actually changed.
  const lastSearchedKey = useRef<string>('');
  // Why the table is empty: a failed search reads very differently from one
  // that genuinely matched nobody.
  const [searchError, setSearchError] = useState<string | null>(null);

  const { selectedIds, toggle, selectAll, clearSelection, selectedCount } = useDataTableSelection({ data: people });

  const columns: ColumnDefinition<Person>[] = [
    { id: 'name', label: 'Name', width: '280px', required: true, sticky: true, skeletonWidth: 'w-32' },
    { id: 'role', label: 'Role', width: '240px', skeletonWidth: 'w-24' },
    { id: 'seniority', label: 'Seniority', width: '150px', skeletonWidth: 'w-24' },
    { id: 'company', label: 'Company', width: '165px', skeletonWidth: 'w-40' },
    { id: 'industry', label: 'Industry', width: '120px', skeletonWidth: 'w-32' },
    { id: 'companyKeywords', label: 'Company Keywords', width: '240px', skeletonWidth: 'w-32' },
    { id: 'companyWebsite', label: 'Company Website', width: '180px', skeletonWidth: 'w-36' },
    { id: 'companySize', label: 'Company Size', width: '130px', skeletonWidth: 'w-16' },
    { id: 'companiesHouse', label: 'Companies House', width: '160px', skeletonWidth: 'w-20' },
    { id: 'emails', label: 'Emails', width: '184px', skeletonWidth: 'w-28' },
    { id: 'location', label: 'Location', width: '192px', skeletonWidth: 'w-28' },
  ];

  const { visibleColumns, saveVisibleColumns } = useDataTableColumns({ columns, storageKey: STORAGE_KEY });

  const [filters, setFilters] = useState<PersonFilters>(() => {
    const initial: PersonFilters = {
      provider: searchParams.get('provider') === 'saved' ? 'saved' : 'prospeo',
      query: searchParams.get('q') || '',
      companyContains: searchParams.get('companyContains') || '',
      scrapedAfter: searchParams.get('scrapedAfter') || '',
      scrapedBefore: searchParams.get('scrapedBefore') || '',
      limit: PAGE_SIZE,
      page: 1,
    };
    for (const key of URL_LIST_PARAMS) {
      // job_titles is spelled job_title in the URL for backwards compatibility
      // with links already shared from this page.
      const param = key === 'job_titles' ? 'job_title' : key;
      (initial as Record<string, unknown>)[key] = searchParams.getAll(param);
    }
    for (const key of URL_NUMBER_PARAMS) {
      const raw = searchParams.get(key);
      const n = raw === null ? NaN : Number(raw);
      if (Number.isFinite(n)) (initial as Record<string, unknown>)[key] = Math.trunc(n);
    }
    const match = searchParams.get('title_match');
    if (match && ['CONTAINS', 'EXACT', 'SIMILAR', 'STRICT'].includes(match)) {
      initial.title_match = match as TitleMatchMode;
    }
    return initial;
  });

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await peopleService.getAllFilterOptions();
        setFilterOptions(options);
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };
    loadFilterOptions();
  }, []);

  const updateURL = useCallback((newFilters: PersonFilters) => {
    const params = new URLSearchParams();
    if (newFilters.query) params.set('q', newFilters.query);
    for (const key of URL_LIST_PARAMS) {
      const param = key === 'job_titles' ? 'job_title' : key;
      (newFilters[key] as string[] | undefined)?.forEach(v => v && params.append(param, v));
    }
    for (const key of URL_NUMBER_PARAMS) {
      const v = newFilters[key];
      if (typeof v === 'number' && Number.isFinite(v)) params.set(key, String(v));
    }
    if (newFilters.title_match && newFilters.title_match !== 'CONTAINS') params.set('title_match', newFilters.title_match);
    if (newFilters.keywords_all) params.set('keywords_all', 'true');
    if (newFilters.companyContains) params.set('companyContains', newFilters.companyContains);
    if (newFilters.scrapedAfter) params.set('scrapedAfter', newFilters.scrapedAfter);
    if (newFilters.scrapedBefore) params.set('scrapedBefore', newFilters.scrapedBefore);
    if (newFilters.provider === 'saved') params.set('provider', 'saved');
    const newURL = params.toString() ? `/dashboard/find-leads?${params.toString()}` : '/dashboard/find-leads';
    navigate(newURL, { replace: true });
  }, [navigate]);

  const searchPeople = useCallback(async (searchFilters: PersonFilters, page: number = 1) => {
    if (loading) return;
    setCurrentPage(page);
    lastSearchedKey.current = filterSignature(searchFilters);

    // Live search costs a credit per page: never fire it without filters.
    if (searchFilters.provider !== 'saved' && !hasProspeoFilters(searchFilters)) {
      setPeople([]);
      setTotal(0);
      setSearchError(null);
      return;
    }

    setLoading(true);
    setSearchError(null);
    try {
      const response = await peopleService.searchPeople({ ...searchFilters, limit: pageSize, page });
      setPeople(response.persons);
      setTotal(response.total);
    } catch (error) {
      console.error('Search failed:', error);
      setPeople([]);
      setTotal(0);
      const err = error as PeopleRequestError;
      if (searchFilters.provider !== 'saved') {
        setSearchError(err.message || 'Prospeo search failed.');
        if (err.code === 'prospeo_unavailable') {
          toast.error('Prospeo is not connected yet. Showing saved people instead.');
          const saved = { ...searchFilters, provider: 'saved' as const };
          setFilters(saved);
          updateURL(saved);
          setLoading(false);
          return searchPeople(saved, page);
        }
        toast.error(err.message || 'Prospeo search failed.');
      } else {
        // The saved table can fail too (people service down, schema drift).
        // Without this the empty state claims there are simply no saved
        // people, which hides a real error behind normal-looking copy.
        const message = err.message || 'Could not load saved people.';
        setSearchError(message);
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  }, [loading, pageSize, updateURL]);

  const totalPages = Math.ceil(total / pageSize);

  useEffect(() => {
    searchPeople(filters, 1);
  }, []);

  const applyFilters = (newFilters: PersonFilters) => {
    const filtersWithPage = { ...newFilters, page: 1 };
    setFilters(filtersWithPage);
    setCurrentPage(1);
    updateURL(filtersWithPage);
    searchPeople(filtersWithPage, 1);
  };

  const clearAllFilters = () => {
    const emptyFilters: PersonFilters = { provider: filters.provider, query: '', limit: PAGE_SIZE, page: 1 };
    setFilters(emptyFilters);
    setCurrentPage(1);
    updateURL(emptyFilters);
    searchPeople(emptyFilters, 1);
  };

  const removeFilter = (type: string, value: string) => {
    const newFilters = { ...filters };
    if (type === 'query') {
      newFilters.query = '';
    } else if (type === 'companyContains') {
      newFilters.companyContains = '';
    } else if ((URL_NUMBER_PARAMS as readonly string[]).includes(type)) {
      newFilters[type as keyof PersonFilters] = undefined as any;
    } else {
      const filterArray = newFilters[type as keyof PersonFilters] as string[];
      if (filterArray) {
        newFilters[type as keyof PersonFilters] = filterArray.filter(v => v !== value) as any;
      }
    }
    setFilters(newFilters);
    setCurrentPage(1);
    updateURL(newFilters);
    searchPeople(newFilters, 1);
  };


  const handleAccessEmail = (person: Person) => {
    setProspectsForCampaign([{ id: person.id, enrichmentStatus: person.enrichment_status, enrichedEmail: person.enriched_email }]);
    setDefaultEnrich(true);
    setAddToCampaignOpen(true);
  };

  const handleBulkAddToCampaign = () => {
    const selectedProspects = people
      .filter((p) => selectedIds.has(p.id))
      .map((p) => ({ id: p.id, enrichmentStatus: p.enrichment_status, enrichedEmail: p.enriched_email }));
    if (selectedProspects.length === 0) {
      toast.error('No prospects selected.');
      return;
    }
    setProspectsForCampaign(selectedProspects);
    setDefaultEnrich(false);
    setAddToCampaignOpen(true);
  };

  const handlePushToCampaign = (person: Person) => {
    setProspectsForCampaign([{ id: person.id, enrichmentStatus: person.enrichment_status, enrichedEmail: person.enriched_email }]);
    setDefaultEnrich(false);
    setAddToCampaignOpen(true);
  };

  const rowActions: RowAction<Person>[] = [
    { label: 'Push to list', onClick: handlePushToCampaign },
  ];

  const columnsWithCells: ColumnDefinition<Person>[] = columns.map(col => {
    if (col.id === 'name') {
      return {
        ...col,
        cell: (person: Person) => (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Checkbox checked={selectedIds.has(person.id)} onCheckedChange={() => toggle(person.id)} />
              {person.profile_image_url
                && !person.profile_image_url.startsWith('data:image/gif')
                && !person.profile_image_url.includes('static.licdn.com') ? (
                <Avatar className="flex-shrink-0 outline-none">
                  <AvatarImage src={person.profile_image_url} alt={`${person.first_name} ${person.last_name}`} />
                  <AvatarFallback className="bg-surface-avatar"><UserIcon className="h-4 w-4 text-muted-foreground" /></AvatarFallback>
                </Avatar>
              ) : (
                <div className="w-8 h-8 rounded-full bg-surface-avatar flex items-center justify-center flex-shrink-0">
                  <UserIcon className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <div className="font-normal text-content-heading text-sm whitespace-nowrap overflow-hidden text-ellipsis" title={`${toTitleCase(person.first_name)} ${toTitleCase(person.last_name)}`}>
                {toTitleCase(person.first_name)} {toTitleCase(person.last_name)}
              </div>
            </div>
            {person.linkedin_url && (
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <a href={person.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 text-primary hover:text-primary/80 transition-colors" onClick={(e) => e.stopPropagation()}>
                      <LinkedInIcon className="h-4 w-4" />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-xs">{person.linkedin_url}</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )
      };
    }
    if (col.id === 'role') return { ...col, accessor: (p: Person) => p.title || '' };
    if (col.id === 'company') return { ...col, accessor: (p: Person) => toTitleCase(p.company?.company_name?.replace(/\s+\b(ltd|limited|plc|llp|lp|cic|inc)\.?\s*$/i, '')) };
    if (col.id === 'companySize') {
      return {
        ...col,
        cell: (person: Person) => {
          const range = person.company?.employee_count_range;
          if (!range) return <span className="text-content-subtle text-sm">-</span>;
          return <span className="text-sm">{range} employees</span>;
        }
      };
    }
    if (col.id === 'industry') return { ...col, accessor: (p: Person) => toTitleCase(p.company?.company_category) };
    if (col.id === 'companyWebsite') {
      return {
        ...col,
        cell: (person: Person) => person.company?.website ? (
          <a href={person.company.website.startsWith('http') ? person.company.website : `https://${person.company.website}`} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-foreground/80 text-sm flex items-center gap-1.5 min-w-0" title={person.company.website} onClick={(e) => e.stopPropagation()}>
            <LinkIcon className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{person.company.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
          </a>
        ) : <span className="text-content-subtle text-sm">-</span>
      };
    }
    if (col.id === 'companyKeywords') {
      return {
        ...col,
        cell: (person: Person) => {
          const keywords = person.sic_keywords;
          if (!keywords || keywords.length === 0) return <span className="text-content-subtle text-sm">-</span>;
          return (
            <div className="relative overflow-hidden -mr-4">
              <div className="flex gap-1 flex-nowrap">
                {keywords.map((kw, i) => (
                  <span key={i} className="inline-block bg-surface-subtle text-content-body-strong text-xs px-2 py-0.5 rounded-full whitespace-nowrap">{kw}</span>
                ))}
              </div>
              <div className="absolute top-0 right-0 bottom-0 w-8 bg-gradient-to-l from-card group-hover:from-table-row-hover group-data-[state=selected]:from-surface-selected to-card/0 pointer-events-none" />
            </div>
          );
        }
      };
    }
    if (col.id === 'companiesHouse') {
      return {
        ...col,
        cell: (person: Person) => {
          const companyName = person.company?.company_name;
          if (person.companies_house_url) {
            return (
              <a href={person.companies_house_url} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-foreground/80 text-sm flex items-center gap-1" title="View on Companies House" onClick={(e) => e.stopPropagation()}>
                <LinkIcon className="h-3 w-3" />
                <GovCrownIcon className="h-3.5 w-3.5" />
              </a>
            );
          }
          if (companyName) {
            const searchUrl = `https://find-and-update.company-information.service.gov.uk/search?q=${encodeURIComponent(companyName)}`;
            return (
              <a href={searchUrl} target="_blank" rel="noopener noreferrer" className="text-content-subtle hover:text-foreground/80 text-sm flex items-center gap-1" title={`Search Companies House for "${companyName}"`} onClick={(e) => e.stopPropagation()}>
                <LinkIcon className="h-3 w-3" />
                <GovCrownIcon className="h-3.5 w-3.5" />
              </a>
            );
          }
          return <span className="text-content-subtle text-sm">-</span>;
        }
      };
    }
    if (col.id === 'emails') {
      return {
        ...col,
        cell: (person: Person) => (
          <Button variant="secondary" size="xs" className="text-sm whitespace-nowrap shadow-xl z-10 bg-card transform-gpu" onClick={() => handleAccessEmail(person)}>
            Find Email
          </Button>
        )
      };
    }
    // Keep compound labels intact: "Founder/Owner", "C-Suite", "Vice President"
    if (col.id === 'seniority') return { ...col, accessor: (p: Person) => (p.seniority_level ?? '').toLowerCase().replace(/(^|[\s/_-])(\w)/g, (_m, sep: string, c: string) => sep + c.toUpperCase()) };
    if (col.id === 'location') return { ...col, accessor: (p: Person) => toTitleCase(p.location) };
    return col;
  });

  const chipRow = (key: keyof PersonFilters, prefix: string): AppliedFilter[] =>
    ((filters[key] as string[] | undefined) || []).map(value => ({ type: key as string, value, label: `${prefix} ${value}`, prefix }));

  const appliedFilters: AppliedFilter[] = [
    ...(filters.query ? [{ type: 'query', value: filters.query, label: `Search "${filters.query}"`, prefix: 'Search' }] : []),
    ...chipRow('location', 'Location is'),
    ...chipRow('location_exclude', 'Location is not'),
    ...chipRow('job_titles', 'Current job title is'),
    ...chipRow('job_titles_exclude', 'Job title is not'),
    ...chipRow('seniority', 'Seniority is'),
    ...chipRow('department', 'Department is'),
    ...chipRow('company_location', 'Company is in'),
    ...chipRow('industry', 'Industry is'),
    ...chipRow('industry_exclude', 'Industry is not'),
    ...chipRow('keywords', 'Company Keyword is'),
    ...chipRow('keywords_exclude', 'Company Keyword is not'),
    ...chipRow('company_names', 'Company is'),
    ...chipRow('company_domains', 'Company domain is'),
    ...chipRow('naics', 'NAICS is'),
    ...chipRow('sic', 'SIC is'),
    ...(filters.companyContains ? [{ type: 'companyContains', value: filters.companyContains, label: `Company contains "${filters.companyContains}"`, prefix: 'Company contains' }] : []),
    ...chipRow('companySize', 'Company size is'),
    ...chipRow('enrichmentStatus', 'Enrichment is'),
    ...numberChip('founded_after', filters.founded_after, 'Founded from'),
    ...numberChip('founded_before', filters.founded_before, 'Founded up to'),
    ...numberChip('experience_min', filters.experience_min, 'Experience from'),
    ...numberChip('experience_max', filters.experience_max, 'Experience up to'),
    ...(filters.scrapedAfter ? [{ type: 'scrapedAfter', value: filters.scrapedAfter, label: `After ${filters.scrapedAfter}`, prefix: 'After' }] : []),
    ...(filters.scrapedBefore ? [{ type: 'scrapedBefore', value: filters.scrapedBefore, label: `Before ${filters.scrapedBefore}`, prefix: 'Before' }] : []),
  ];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const { scrollLeft, scrollWidth, clientWidth } = target;
    setShowLeftGradient(scrollLeft > 0);
    setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 1);
  };

  const smoothScroll = (element: HTMLElement, distance: number, duration = 300) => {
    const start = element.scrollLeft;
    const startTime = performance.now();
    const step = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      element.scrollLeft = start + distance * ease;
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const scrollLeft = () => {
    const scrollContainer = document.querySelector('.filter-scroll-container') as HTMLDivElement;
    if (scrollContainer) smoothScroll(scrollContainer, -400);
  };

  const scrollRight = () => {
    const scrollContainer = document.querySelector('.filter-scroll-container') as HTMLDivElement;
    if (scrollContainer) smoothScroll(scrollContainer, 400);
  };

  useEffect(() => {
    const scrollContainer = document.querySelector('.filter-scroll-container') as HTMLDivElement;
    if (scrollContainer) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
      setShowLeftGradient(scrollLeft > 0);
      setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, [appliedFilters]);

  return (
    <section className="relative h-screen overflow-hidden flex flex-col">
      <div className="flex gap-3 items-center flex-shrink-0 mt-page-top pr-gutter">
        <PageHeader title="Find Leads"/>
        {/* Brings the filter sidebar back; the sidebar hides itself. */}
        {!filtersOpen && <Button variant="primary" onClick={() => setFiltersOpen(true)}>Open Search & Filters</Button>}
        {/* The applied filters, as removable chips. The open sidebar already
            shows them, so they only appear while it is closed. */}
        {!filtersOpen && appliedFilters.length > 0 && (
          <div className="relative flex-1 min-w-0">
            {showLeftGradient && (
              <button onClick={scrollLeft} className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 bg-surface-raised/80 hover:bg-surface-raised border border-control-border flex items-center justify-center z-20 transition-colors rounded-full shadow-xl cursor-pointer backdrop-blur-sm" aria-label="Scroll left">
                <ChevronLeftIcon className="h-4 w-4 text-content-secondary" />
              </button>
            )}
            <div className="filter-scroll-container flex gap-2 overflow-x-auto scrollbar-hide shadow-inner max-w-full pr-12" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}} onScroll={handleScroll}>
              {appliedFilters.map((filter, index) => (
                <button key={`${filter.type}-${filter.value}-${index}`} onClick={() => removeFilter(filter.type, filter.value)} className="flex items-center gap-2 bg-info-surface-strong text-info-foreground px-4 py-2 rounded-full text-sm h-12 whitespace-nowrap flex-shrink-0 cursor-pointer hover:bg-info-surface-hover transition-colors" aria-label={`Remove ${filter.label} filter`}>
                  <span>{filter.prefix} <span className="font-semibold">{filter.value}</span></span>
                  <XMarkIcon className="h-4 w-4 text-info-foreground-deep" />
                </button>
              ))}
            </div>
            {showRightGradient && (
              <button onClick={scrollRight} className="absolute right-5 top-1/2 -translate-y-1/2 w-8 h-8 bg-surface-raised/80 hover:bg-surface-raised border border-control-border flex items-center justify-center z-20 transition-colors rounded-full shadow-xl cursor-pointer backdrop-blur-sm" aria-label="Scroll right">
                <ChevronRightIcon className="h-4 w-4 text-content-secondary" />
              </button>
            )}
            <div className={`absolute left-0 top-0 bottom-2 w-30 h-full bg-gradient-to-r from-background to-background/0 pointer-events-none z-10 transition-opacity duration-0 ${showLeftGradient ? 'opacity-100' : 'opacity-0'}`}></div>
            <div className={`absolute right-0 top-0 bottom-2 w-30 h-full bg-gradient-to-l from-background to-background/0 pointer-events-none z-10 transition-opacity duration-0 ${showRightGradient ? 'opacity-100' : 'opacity-0'}`}></div>
          </div>
        )}
      </div>

      {/* Filters and table share one flush row under the header rule: the
          filter sidebar on the left behind a single rule, the table and its
          footer on the right. */}
      <div className="mt-header-gap flex-1 min-h-0 flex border-t border-table-grid">
        {filtersOpen && (
          <FilterSidebar filters={filters} setFilters={setFilters} filterOptions={filterOptions} onApply={applyFilters} onClear={clearAllFilters} onHide={() => {
            // Hiding runs the search if the filters changed, so they never sit
            // applied without results behind them. Unchanged filters don't
            // re-search.
            if (filterSignature(filters) !== lastSearchedKey.current) applyFilters(filters);
            setFiltersOpen(false);
          }} />
        )}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="bg-card flex-1 min-h-0 border-b border-table-grid overflow-hidden relative flex flex-col" ref={tableContainerRef} style={{ contain: 'layout paint' }}>
            <DataTable
              data={people}
              columns={columnsWithCells}
              isLoading={loading}
              resizable
              resizeConfig={{ storageKey: 'findPeople_columnWidths' }}
              selection={{ selectedIds, onToggle: toggle, onSelectAll: selectAll, onClearSelection: clearSelection }}
              rowActions={rowActions}
              columnVisibility={{ visibleColumns, onSave: saveVisibleColumns, storageKey: STORAGE_KEY }}
              emptyState={searchError ? (
                <div className="text-center max-w-md">
                  <p className="text-foreground mb-4">{searchError}</p>
                  <Button variant="primary" onClick={() => searchPeople(filters, currentPage)}>Try again</Button>
                </div>
              ) : filters.provider !== 'saved' && hasProspeoFilters(filters) && filterSignature(filters) !== lastSearchedKey.current ? (
                // The sidebar sits beside the table, so filters can change
                // before a search runs. Say so rather than claim no matches.
                <div className="text-center">
                  <p className="text-foreground mb-4">Search to see people matching these filters.</p>
                  <Button variant="primary" onClick={() => applyFilters(filters)}>Search</Button>
                </div>
              ) : appliedFilters.length > 0 ? (
                <div className="text-center">
                  <p className="text-foreground mb-4">No people found matching your criteria</p>
                  <Button variant="primary" onClick={clearAllFilters}>Clear filters</Button>
                </div>
              ) : filters.provider !== 'saved' ? (
                <div className="text-center">
                  <p className={`text-foreground${filtersOpen ? '' : ' mb-4'}`}>Apply filters to view people.</p>
                  {!filtersOpen && <Button variant="primary" onClick={() => setFiltersOpen(true)}>Open Search & Filters</Button>}
                </div>
              ) : <p className="text-foreground text-center">No saved people yet.</p>}
            />
          </div>

          <DataTablePagination
            config={{ variant: 'animated', currentPage, totalPages, onPageChange: (page) => searchPeople(filters, page) }}
            totalLabel="results found"
            total={total}
          />
        </div>
      </div>

      <SelectionActionBar selectedCount={selectedCount} isVisible={selectedCount > 0} onClearSelection={clearSelection} itemName={{ singular: 'person', plural: 'people' }} tableContainerRef={tableContainerRef} selectedRowIds={Array.from(selectedIds) as string[]}>
        <Button variant="primary" size="xs" onClick={handleBulkAddToCampaign}>Push to List</Button>
      </SelectionActionBar>

      <AddToCampaignModal open={addToCampaignOpen} onOpenChange={(open) => { setAddToCampaignOpen(open); if (!open) setProspectsForCampaign([]); }} prospects={prospectsForCampaign} defaultEnrichEmails={defaultEnrich} onSuccess={() => { clearSelection(); setProspectsForCampaign([]); searchPeople(filters, currentPage); }} />

      {showLimitModal && (
        <div className="fixed inset-0 bg-scrim/50 flex items-center justify-center z-50 p-4">
          <div className="max-w-md w-full"><TrialLimitReached onClose={() => setShowLimitModal(false)} /></div>
        </div>
      )}
    </section>
  );
}

interface FilterPanelProps {
  title: string;
  panelId: string;
  isExpanded: boolean;
  onToggle: (panelId: string) => void;
  appliedFilters?: Array<{ value: string; label: string }>;
  onRemoveFilter?: (value: string) => void;
  children: React.ReactNode;
}

function FilterPanel({ title, panelId, isExpanded, onToggle, appliedFilters = [], onRemoveFilter, children }: FilterPanelProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [doneAnimating, setDoneAnimating] = useState(false);

  useEffect(() => {
    if (isExpanded && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
      // Allow overflow after expand animation finishes
      const timer = setTimeout(() => setDoneAnimating(true), 220);
      return () => clearTimeout(timer);
    } else {
      setDoneAnimating(false);
      setHeight(0);
    }
  }, [isExpanded, children]);

  const handlePanelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && isExpanded) {
      e.stopPropagation();
      onToggle(panelId);
    }
  };

  return (
    <div className={`border border-panel-border rounded-inner bg-card ${isExpanded && doneAnimating ? '' : 'overflow-hidden'}`} onKeyDown={handlePanelKeyDown}>
      <button onClick={() => onToggle(panelId)} className="w-full px-4 py-3 text-left text-sm font-medium flex items-center justify-between cursor-pointer">
        <span>{title}</span>
        <ChevronDownIcon className={`h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      {appliedFilters.length > 0 && (
        <div className="px-4 -mt-1 pb-3 flex flex-wrap gap-1.5">
          {appliedFilters.map((filter, index) => (
            <button onClick={(e) => { e.stopPropagation(); onRemoveFilter && onRemoveFilter(filter.value); }} key={`${filter.value}-${index}`} className="inline-flex items-center gap-1 bg-info-surface-strong text-info-foreground-deep pr-1.5 pl-2.5 py-1 rounded-md text-sm cursor-pointer hover:bg-info-surface-hover transition-colors">
              <span>{filter.label}</span>
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      )}
      <div className={isExpanded && doneAnimating ? '' : 'overflow-hidden'} style={{ height: isExpanded && doneAnimating ? 'auto' : height, transition: !doneAnimating && isExpanded ? 'height 200ms ease-out' : 'none' }}>
        <div ref={contentRef} className="px-3 pb-3 space-y-1">{children}</div>
      </div>
    </div>
  );
}

/** The compact field used inside the filter sidebar's sections. */
const SIDEBAR_INPUT = 'w-full h-9 px-4 py-2 text-sm mb-0';

interface FilterSidebarProps {
  filters: PersonFilters;
  setFilters: (filters: PersonFilters) => void;
  filterOptions: FilterOptions;
  onApply: (newFilters: PersonFilters) => void;
  onClear: () => void;
  onHide: () => void;
}

function FilterSidebar({ filters, setFilters, filterOptions, onApply, onClear, onHide }: FilterSidebarProps) {
  const [expandedPanels, setExpandedPanels] = useState<Record<string, boolean>>({});
  const [jobTitleSearch, setJobTitleSearch] = useState('');

  // Live mode: every distinct filter combination is a paid Prospeo query, so
  // changes only apply on Search rather than on each click.
  const live = filters.provider !== 'saved';
  const seniorityOptions = live ? PROSPEO_SENIORITIES : (filterOptions?.seniorities?.slice(0, 10) || []);
  const companySizeOptions = live ? PROSPEO_COMPANY_SIZES : (filterOptions?.companySizes || []);

  const updateFilter = (key: keyof PersonFilters, value: any) => {
    const newFilters = { ...filters, [key]: value, offset: 0 };
    setFilters(newFilters);
    if (!live) onApply(newFilters);
  };

  const togglePanel = (panelId: string) => {
    setExpandedPanels(prev => ({ ...prev, [panelId]: !prev[panelId] }));
  };

  const list = (key: keyof PersonFilters): string[] => (filters[key] as string[] | undefined) || [];
  const chipsFor = (key: keyof PersonFilters) => list(key).map(v => ({ value: v, label: v }));
  // A combobox renders its own tags, so the panel header only repeats them
  // once it is collapsed.
  const chipsWhenCollapsed = (panelId: string, key: keyof PersonFilters) =>
    expandedPanels[panelId] ? [] : chipsFor(key);

  const numberValue = (key: keyof PersonFilters): string => {
    const v = filters[key];
    return typeof v === 'number' && Number.isFinite(v) ? String(v) : '';
  };
  const setNumber = (key: keyof PersonFilters, raw: string) => {
    const trimmed = raw.trim();
    const n = Number(trimmed);
    updateFilter(key, trimmed && Number.isFinite(n) ? Math.trunc(n) : undefined);
  };

  const appliedFiltersCount = [
    filters.query && filters.query.trim() ? 1 : 0,
    filters.location?.length || 0,
    filters.location_exclude?.length || 0,
    filters.job_titles?.length || 0,
    filters.job_titles_exclude?.length || 0,
    filters.industry?.length || 0,
    filters.industry_exclude?.length || 0,
    filters.seniority?.length || 0,
    filters.department?.length || 0,
    filters.keywords?.length || 0,
    filters.keywords_exclude?.length || 0,
    filters.company_location?.length || 0,
    filters.company_names?.length || 0,
    filters.company_domains?.length || 0,
    filters.naics?.length || 0,
    filters.sic?.length || 0,
    filters.companyContains ? 1 : 0,
    filters.companySize?.length || 0,
    filters.enrichmentStatus?.length || 0,
    filters.scrapedAfter ? 1 : 0,
    filters.scrapedBefore ? 1 : 0,
    typeof filters.founded_after === 'number' ? 1 : 0,
    typeof filters.founded_before === 'number' ? 1 : 0,
    typeof filters.experience_min === 'number' ? 1 : 0,
    typeof filters.experience_max === 'number' ? 1 : 0,
  ].reduce((sum, count) => sum + count, 0);

  const removeFilter = (type: string, value: string) => {
    if (type === 'query') updateFilter('query', '');
    else if (type === 'companyContains') updateFilter('companyContains', '');
    else updateFilter(type as keyof PersonFilters, list(type as keyof PersonFilters).filter(v => v !== value));
  };

  return (
    <aside className="w-[22rem] shrink-0 border-r border-table-grid flex flex-col min-h-0">
      <div className="shrink-0 flex items-center justify-between px-gutter-lg pt-gutter-lg">
        <p className="text-sm font-semibold">Filters</p>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="secondary" size="icon" onClick={onHide} className="!size-10 !p-0" aria-label="Hide filters">
              <PanelLeft className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10}>Hide filters</TooltipContent>
        </Tooltip>
      </div>
      <PageScrollArea>
        <div className="space-y-1.5 px-gutter-lg pb-6">
          {live ? (
            <>
              <FilterSectionHeading>Person</FilterSectionHeading>

              <FilterPanel title="Job Titles" panelId="job_titles" isExpanded={expandedPanels.job_titles} onToggle={togglePanel} appliedFilters={chipsWhenCollapsed('job_titles', 'job_titles')} onRemoveFilter={(value) => removeFilter('job_titles', value)}>
                <Combobox
                  size="sm"
                  options={[]}
                  onSearch={SUGGEST_JOB_TITLE}
                  value={filters.job_titles || []}
                  onChange={(selected) => updateFilter('job_titles', selected)}
                  placeholder="Type a job title, e.g. Founder"
                  emptyMessage="No matching titles. Press Enter to use yours."
                  allowCustom
                />
                <p className="text-sm pt-1">How titles match</p>
                <div className="flex flex-wrap gap-1 pb-1">
                  {TITLE_MATCH_MODES.map(mode => {
                    const active = (filters.title_match || 'CONTAINS') === mode.value;
                    return (
                      <button
                        key={mode.value}
                        type="button"
                        onClick={() => updateFilter('title_match', mode.value)}
                        className={`px-2.5 py-1 rounded-full text-xs cursor-pointer transition-colors ${active ? 'bg-active !text-primary' : 'hover:bg-accent'}`}
                        title={mode.hint}
                      >
                        {mode.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-sm">{TITLE_MATCH_MODES.find(m => m.value === (filters.title_match || 'CONTAINS'))?.hint}</p>
                <p className="text-sm pt-2">Exclude titles</p>
                <Combobox
                  size="sm"
                  options={[]}
                  onSearch={SUGGEST_JOB_TITLE}
                  value={filters.job_titles_exclude || []}
                  onChange={(selected) => updateFilter('job_titles_exclude', selected)}
                  placeholder="e.g. Assistant"
                  emptyMessage="No matching titles. Press Enter to use yours."
                  allowCustom
                />
              </FilterPanel>

              <FilterPanel title="Seniority" panelId="seniority" isExpanded={expandedPanels.seniority} onToggle={togglePanel} appliedFilters={chipsFor('seniority')} onRemoveFilter={(value) => removeFilter('seniority', value)}>
                {seniorityOptions.map((seniority) => (
                  <div key={seniority} className="flex items-center gap-1.5">
                    <Checkbox id={`seniority-${seniority}`} checked={(filters.seniority || []).includes(seniority)} onCheckedChange={(checked) => { const current = filters.seniority || []; updateFilter('seniority', checked ? [...current, seniority] : current.filter(s => s !== seniority)); }} />
                    <label htmlFor={`seniority-${seniority}`} className="text-sm">{seniority}</label>
                  </div>
                ))}
              </FilterPanel>

              <FilterPanel title="Department" panelId="department" isExpanded={expandedPanels.department} onToggle={togglePanel} appliedFilters={chipsFor('department')} onRemoveFilter={(value) => removeFilter('department', value)}>
                {PROSPEO_DEPARTMENTS.map((dept) => (
                  <div key={dept} className="flex items-center gap-1.5">
                    <Checkbox id={`department-${dept}`} checked={(filters.department || []).includes(dept)} onCheckedChange={(checked) => { const current = filters.department || []; updateFilter('department', checked ? [...current, dept] : current.filter(d => d !== dept)); }} />
                    <label htmlFor={`department-${dept}`} className="text-sm">{dept}</label>
                  </div>
                ))}
              </FilterPanel>

              <FilterPanel title="Location" panelId="location" isExpanded={expandedPanels.location} onToggle={togglePanel} appliedFilters={chipsWhenCollapsed('location', 'location')} onRemoveFilter={(value) => removeFilter('location', value)}>
                <Combobox
                  size="sm"
                  options={[]}
                  onSearch={SUGGEST_LOCATION}
                  value={filters.location || []}
                  onChange={(selected) => updateFilter('location', selected)}
                  placeholder="Type a town, city or country"
                  emptyMessage="No matching places."
                />
                <p className="text-sm">Where the person is, not the company. Pick from the list so Prospeo recognises it.</p>
                <p className="text-sm pt-2">Exclude places</p>
                <Combobox
                  size="sm"
                  options={[]}
                  onSearch={SUGGEST_LOCATION}
                  value={filters.location_exclude || []}
                  onChange={(selected) => updateFilter('location_exclude', selected)}
                  placeholder="e.g. London, United Kingdom"
                  emptyMessage="No matching places."
                />
              </FilterPanel>

              <FilterPanel title="Years of Experience" panelId="experience" isExpanded={expandedPanels.experience} onToggle={togglePanel} appliedFilters={rangeChips(filters.experience_min, filters.experience_max, 'years')} onRemoveFilter={() => { updateFilter('experience_min', undefined); updateFilter('experience_max', undefined); }}>
                <div className="flex items-center gap-3">
                  <Input type="number" min={0} max={70} placeholder="Min" className={SIDEBAR_INPUT} value={numberValue('experience_min')} onChange={(e) => setNumber('experience_min', e.target.value)} />
                  <span className="text-sm">to</span>
                  <Input type="number" min={0} max={70} placeholder="Max" className={SIDEBAR_INPUT} value={numberValue('experience_max')} onChange={(e) => setNumber('experience_max', e.target.value)} />
                </div>
              </FilterPanel>

              <FilterSectionHeading>Company</FilterSectionHeading>

              <FilterPanel title="Company Location" panelId="company_location" isExpanded={expandedPanels.company_location} onToggle={togglePanel} appliedFilters={chipsWhenCollapsed('company_location', 'company_location')} onRemoveFilter={(value) => removeFilter('company_location', value)}>
                <Combobox
                  size="sm"
                  options={[]}
                  onSearch={SUGGEST_LOCATION}
                  value={filters.company_location || []}
                  onChange={(selected) => updateFilter('company_location', selected)}
                  placeholder="Where the company is headquartered"
                  emptyMessage="No matching places."
                />
                <p className="text-sm">Leave empty to include UK based people working for companies headquartered abroad.</p>
              </FilterPanel>

              <FilterPanel title="Company Size" panelId="companySize" isExpanded={expandedPanels.companySize} onToggle={togglePanel} appliedFilters={chipsFor('companySize')} onRemoveFilter={(value) => removeFilter('companySize', value)}>
                {companySizeOptions.map((option) => (
                  <div key={option.value} className="flex items-center gap-1.5">
                    <Checkbox id={`companySize-${option.value}`} checked={(filters.companySize || []).includes(option.value)} onCheckedChange={(checked) => { const current = filters.companySize || []; updateFilter('companySize', checked ? [...current, option.value] : current.filter(s => s !== option.value)); }} />
                    <label htmlFor={`companySize-${option.value}`} className="text-sm">{option.label} employees</label>
                  </div>
                ))}
              </FilterPanel>

              <FilterPanel title="Company Industry" panelId="industry" isExpanded={expandedPanels.industry} onToggle={togglePanel} appliedFilters={chipsWhenCollapsed('industry', 'industry')} onRemoveFilter={(val) => removeFilter('industry', val)}>
                <Combobox
                  size="sm"
                  options={[]}
                  onSearch={SUGGEST_INDUSTRY}
                  value={filters.industry || []}
                  onChange={(selected) => updateFilter('industry', selected)}
                  placeholder="Search industries, e.g. software"
                  emptyMessage="No matching industries."
                />
                <p className="text-sm pt-2">Exclude industries</p>
                <Combobox
                  size="sm"
                  options={[]}
                  onSearch={SUGGEST_INDUSTRY}
                  value={filters.industry_exclude || []}
                  onChange={(selected) => updateFilter('industry_exclude', selected)}
                  placeholder="e.g. staffing"
                  emptyMessage="No matching industries."
                />
              </FilterPanel>

              <FilterPanel title="Company Keywords" panelId="keywords" isExpanded={expandedPanels.keywords} onToggle={togglePanel} appliedFilters={chipsWhenCollapsed('keywords', 'keywords')} onRemoveFilter={(val) => removeFilter('keywords', val)}>
                <Combobox
                  size="sm"
                  options={[]}
                  value={filters.keywords || []}
                  onChange={(selected) => updateFilter('keywords', selected)}
                  placeholder="e.g. commercial cleaning"
                  emptyMessage="Press Enter to add a keyword."
                  allowCustom
                />
                <div className="flex items-center gap-1.5 pt-1">
                  <Checkbox id="keywords_all" checked={!!filters.keywords_all} onCheckedChange={(checked) => updateFilter('keywords_all', !!checked)} />
                  <label htmlFor="keywords_all" className="text-sm">Company must match every keyword</label>
                </div>
                <p className="text-sm">Searched across the company name, description and tags. Three characters minimum.</p>
                <p className="text-sm pt-2">Exclude keywords</p>
                <Combobox
                  size="sm"
                  options={[]}
                  value={filters.keywords_exclude || []}
                  onChange={(selected) => updateFilter('keywords_exclude', selected)}
                  placeholder="e.g. recruitment"
                  emptyMessage="Press Enter to add a keyword."
                  allowCustom
                />
              </FilterPanel>

              <FilterPanel title="Company Name" panelId="company_names" isExpanded={expandedPanels.company_names} onToggle={togglePanel} appliedFilters={chipsWhenCollapsed('company_names', 'company_names')} onRemoveFilter={(val) => removeFilter('company_names', val)}>
                <Combobox
                  size="sm"
                  options={[]}
                  value={filters.company_names || []}
                  onChange={(selected) => updateFilter('company_names', selected)}
                  placeholder="Type a company name and press Enter"
                  emptyMessage="Press Enter to add this company."
                  allowCustom
                />
              </FilterPanel>

              <FilterPanel title="Company Domain" panelId="company_domains" isExpanded={expandedPanels.company_domains} onToggle={togglePanel} appliedFilters={chipsWhenCollapsed('company_domains', 'company_domains')} onRemoveFilter={(val) => removeFilter('company_domains', val)}>
                <Combobox
                  size="sm"
                  options={[]}
                  value={filters.company_domains || []}
                  onChange={(selected) => updateFilter('company_domains', selected)}
                  placeholder="e.g. lopay.com"
                  emptyMessage="Press Enter to add this domain."
                  allowCustom
                />
                <p className="text-sm">Target a known list of companies by their websites.</p>
              </FilterPanel>

              <FilterPanel title="Industry Codes" panelId="codes" isExpanded={expandedPanels.codes} onToggle={togglePanel} appliedFilters={expandedPanels.codes ? [] : [...chipsFor('naics'), ...chipsFor('sic')]} onRemoveFilter={(val) => { removeFilter('naics', val); removeFilter('sic', val); }}>
                <p className="text-sm">NAICS</p>
                <Combobox
                  size="sm"
                  options={[]}
                  onSearch={SUGGEST_NAICS}
                  value={filters.naics || []}
                  onChange={(selected) => updateFilter('naics', selected)}
                  placeholder="Search by code or description"
                  emptyMessage="No matching codes."
                />
                <p className="text-sm pt-2">SIC</p>
                <Combobox
                  size="sm"
                  options={[]}
                  onSearch={SUGGEST_SIC}
                  value={filters.sic || []}
                  onChange={(selected) => updateFilter('sic', selected)}
                  placeholder="Search by code or description"
                  emptyMessage="No matching codes."
                />
                <p className="text-sm">These are the US classification systems, not Companies House SIC codes.</p>
              </FilterPanel>

              <FilterPanel title="Founded" panelId="founded" isExpanded={expandedPanels.founded} onToggle={togglePanel} appliedFilters={rangeChips(filters.founded_after, filters.founded_before, '')} onRemoveFilter={() => { updateFilter('founded_after', undefined); updateFilter('founded_before', undefined); }}>
                <div className="flex items-center gap-3">
                  <Input type="number" min={1800} max={2100} placeholder="From year" className={SIDEBAR_INPUT} value={numberValue('founded_after')} onChange={(e) => setNumber('founded_after', e.target.value)} />
                  <span className="text-sm">to</span>
                  <Input type="number" min={1800} max={2100} placeholder="To year" className={SIDEBAR_INPUT} value={numberValue('founded_before')} onChange={(e) => setNumber('founded_before', e.target.value)} />
                </div>
              </FilterPanel>

              <FilterPanel title="People Per Company" panelId="max_per_company" isExpanded={expandedPanels.max_per_company} onToggle={togglePanel} appliedFilters={typeof filters.max_per_company === 'number' ? [{ value: 'max', label: `Up to ${filters.max_per_company} per company` }] : []} onRemoveFilter={() => updateFilter('max_per_company', undefined)}>
                <Input type="number" min={1} max={100} placeholder="2" className={SIDEBAR_INPUT} value={numberValue('max_per_company')} onChange={(e) => setNumber('max_per_company', e.target.value)} />
                <p className="text-sm">Caps how many people from the same company can fill a page. Defaults to 2.</p>
              </FilterPanel>
            </>
          ) : (
            <>
              <FilterPanel title="Location" panelId="location" isExpanded={expandedPanels.location} onToggle={togglePanel} appliedFilters={chipsFor('location')} onRemoveFilter={(value) => removeFilter('location', value)}>
                {filterOptions?.locations?.slice(0, 10).map((location) => (
                  <div key={location} className="flex items-center gap-1.5">
                    <Checkbox id={`location-${location}`} checked={(filters.location || []).includes(location)} onCheckedChange={(checked) => { const currentLocations = filters.location || []; updateFilter('location', checked ? [...currentLocations, location] : currentLocations.filter(l => l !== location)); }} />
                    <label htmlFor={`location-${location}`} className="text-sm">{location}</label>
                  </div>
                ))}
              </FilterPanel>
              <FilterPanel title="Job Titles" panelId="job_titles" isExpanded={expandedPanels.job_titles} onToggle={togglePanel} appliedFilters={chipsFor('job_titles')} onRemoveFilter={(value) => removeFilter('job_titles', value)}>
                <Input type="text" placeholder="Search job titles..." value={jobTitleSearch} onChange={(e) => setJobTitleSearch(e.target.value)} className="w-full" />
                {filterOptions?.job_titles?.filter(title => title.toLowerCase().includes(jobTitleSearch.toLowerCase())).map((title) => (
                  <div key={title} className="flex items-center space-x-2 py-1">
                    <Checkbox id={`title-${title}`} checked={(filters.job_titles || []).includes(title)} onCheckedChange={(checked) => { const currentTitles = filters.job_titles || []; updateFilter('job_titles', checked ? [...currentTitles, title] : currentTitles.filter(t => t !== title)); }} />
                    <label htmlFor={`title-${title}`} className="text-sm cursor-pointer">{title}</label>
                  </div>
                ))}
                {filterOptions?.job_titles?.filter(title => title.toLowerCase().includes(jobTitleSearch.toLowerCase())).length === 0 && <p className="text-sm py-2">No matching titles</p>}
              </FilterPanel>
              <FilterPanel title="Seniority" panelId="seniority" isExpanded={expandedPanels.seniority} onToggle={togglePanel} appliedFilters={chipsFor('seniority')} onRemoveFilter={(value) => removeFilter('seniority', value)}>
                {seniorityOptions.map((seniority) => (
                  <div key={seniority} className="flex items-center gap-1.5">
                    <Checkbox id={`seniority-${seniority}`} checked={(filters.seniority || []).includes(seniority)} onCheckedChange={(checked) => { const current = filters.seniority || []; updateFilter('seniority', checked ? [...current, seniority] : current.filter(s => s !== seniority)); }} />
                    <label htmlFor={`seniority-${seniority}`} className="text-sm">{seniority}</label>
                  </div>
                ))}
              </FilterPanel>
              <FilterPanel title="Company Industry" panelId="industry" isExpanded={expandedPanels.industry} onToggle={togglePanel} appliedFilters={chipsWhenCollapsed('industry', 'industry')} onRemoveFilter={(val) => removeFilter('industry', val)}>
                <Combobox
                  size="sm"
                  options={(filterOptions?.industries || []).map((ind) => ({ value: ind, label: ind }))}
                  value={filters.industry || []}
                  onChange={(selected) => updateFilter('industry', selected)}
                  placeholder="Search industries..."
                  emptyMessage="No matching industries."
                />
              </FilterPanel>
              <FilterPanel title="Company Keywords" panelId="keywords" isExpanded={expandedPanels.keywords} onToggle={togglePanel} appliedFilters={chipsWhenCollapsed('keywords', 'keywords')} onRemoveFilter={(val) => removeFilter('keywords', val)}>
                <Combobox
                  size="sm"
                  options={(filterOptions?.keywords || []).map((kw) => ({ value: kw, label: kw }))}
                  value={filters.keywords || []}
                  onChange={(selected) => updateFilter('keywords', selected)}
                  placeholder="e.g. commercial cleaning..."
                  emptyMessage="No matching keywords."
                  allowCustom
                />
              </FilterPanel>
              <FilterPanel title="Company Name" panelId="companyName" isExpanded={expandedPanels.companyName} onToggle={togglePanel} appliedFilters={filters.companyContains ? [{ value: filters.companyContains, label: filters.companyContains }] : []} onRemoveFilter={() => updateFilter('companyContains', '')}>
                <Input type="text" placeholder="Search company name..." className={SIDEBAR_INPUT} value={filters.companyContains || ''} onChange={(e) => updateFilter('companyContains', e.target.value)} />
              </FilterPanel>
              <FilterPanel title="Company Size" panelId="companySize" isExpanded={expandedPanels.companySize} onToggle={togglePanel} appliedFilters={filters.companySize?.map(size => { const opt = companySizeOptions.find(s => s.value === size); return { value: size, label: opt?.label || size }; }) || []} onRemoveFilter={(value) => removeFilter('companySize', value)}>
                {companySizeOptions.map((option) => (
                  <div key={option.value} className="flex items-center gap-1.5">
                    <Checkbox id={`companySize-${option.value}`} checked={(filters.companySize || []).includes(option.value)} onCheckedChange={(checked) => { const current = filters.companySize || []; updateFilter('companySize', checked ? [...current, option.value] : current.filter(s => s !== option.value)); }} />
                    <label htmlFor={`companySize-${option.value}`} className="text-sm">{option.label}</label>
                  </div>
                ))}
              </FilterPanel>
            </>
          )}
        </div>
      </PageScrollArea>
      {/* Live search is paid per page, so it only runs on Search. Saved-mode
          filters apply as they change and need no button. */}
      {(live || appliedFiltersCount > 0) && (
        // Same vertical build as TableFooter (gutter, table-footer row,
        // gutter), so this rule lands level with the table's bottom rule.
        <div className="shrink-0 border-t border-table-grid px-gutter-lg">
          <div className="my-gutter min-h-table-footer flex items-center justify-between gap-3">
            {appliedFiltersCount > 0 ? <Button variant="secondary" onClick={onClear}>Clear all</Button> : <div />}
            {live && <Button variant="primary" onClick={() => onApply(filters)}>Search</Button>}
          </div>
        </div>
      )}
    </aside>
  );
}

function FindPeoplePageWithSuspense() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <FindPeoplePage />
    </Suspense>
  );
}

export default FindPeoplePageWithSuspense;
